import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Phone, MessageSquare, AlertTriangle, RefreshCw, ArrowUpRight,
  ArrowDownRight, DollarSign, CheckCircle2, XCircle, Copy, Loader2,
} from "lucide-react";

const STATUS_BADGE = {
  delivered:   "bg-emerald-100 text-emerald-900 border-emerald-300",
  sent:        "bg-amber-50 text-amber-900 border-amber-200",
  queued:      "bg-slate-100 text-slate-700 border-slate-300",
  failed:      "bg-rose-100 text-rose-900 border-rose-300",
  undelivered: "bg-rose-100 text-rose-900 border-rose-300",
  received:    "bg-violet-100 text-violet-900 border-violet-300",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const copyToClipboard = async (text, label = "URL") => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiée`);
  } catch { toast.error("Copie impossible"); }
};

export default function AdminTwilio() {
  const [overview, setOverview] = useState(null);
  const [messages, setMessages] = useState([]);
  const [errors, setErrors] = useState([]);
  const [inboundConfig, setInboundConfig] = useState(null);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, msgs, errs, ib] = await Promise.all([
        apiClient.get(`/admin/twilio/overview`),
        apiClient.get(`/admin/twilio/messages?limit=30`),
        apiClient.get(`/admin/twilio/errors?limit=20`),
        apiClient.get(`/admin/twilio/inbound-config`),
      ]);
      setOverview(ov.data);
      setMessages(msgs.data.messages || []);
      setErrors(errs.data.errors || []);
      setInboundConfig(ib.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Twilio injoignable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-32 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement Twilio…
        </div>
      </div>
    );
  }

  const accountType = overview?.account?.type || "—";
  const isTrial = accountType === "Trial";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <header className="mb-8 flex items-start justify-between gap-4 flex-wrap" data-testid="admin-twilio-header">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Cockpit Admin</p>
            <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900 mt-1">
              Monitoring Twilio
            </h1>
            <p className="text-slate-600 mt-2 text-base">
              Compte actif, solde, derniers SMS, erreurs, configuration des webhooks entrants.
            </p>
          </div>
          <Button onClick={refresh} variant="outline" data-testid="twilio-refresh" className="shrink-0">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </Button>
        </header>

        {/* KPI Top row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="twilio-kpis">
          <Card className="p-4 border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Compte</div>
            <div className="font-serif text-lg text-slate-900 mt-1 truncate">
              {overview?.account?.friendly_name || "—"}
            </div>
            <Badge className={isTrial ? "bg-amber-100 text-amber-900 border-amber-300 mt-2"
                                       : "bg-emerald-100 text-emerald-900 border-emerald-300 mt-2"}>
              {accountType}
            </Badge>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
              <DollarSign className="w-3 h-3" /> Solde
            </div>
            <div className="font-serif text-3xl text-slate-900 mt-1">
              {overview?.balance?.amount
                ? `${parseFloat(overview.balance.amount).toFixed(2)} ${overview.balance.currency}`
                : "—"}
            </div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
              <ArrowUpRight className="w-3 h-3" /> Sortants 24h
            </div>
            <div className="font-serif text-3xl text-slate-900 mt-1">
              {overview?.confilex_kpis_24h?.outbound_messages ?? 0}
            </div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
              <ArrowDownRight className="w-3 h-3" /> Entrants 24h
            </div>
            <div className="font-serif text-3xl text-slate-900 mt-1">
              {overview?.confilex_kpis_24h?.inbound_messages ?? 0}
            </div>
          </Card>
        </div>

        {isTrial && (
          <Card className="p-4 mb-6 bg-amber-50 border-amber-300 border" data-testid="trial-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-medium text-amber-900">Compte Twilio en mode Trial</h3>
                <p className="text-sm text-amber-800 mt-1">
                  Les SMS partent uniquement vers les numéros vérifiés et contiennent le préfixe "Sent from Twilio trial".
                  Passez en Pay-As-You-Go pour lever ces limitations : <a href="https://console.twilio.com/us1/billing/manage-billing/billing-overview" target="_blank" rel="noreferrer" className="underline">Upgrade billing</a>.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Numbers */}
        <Card className="p-5 mb-6 border-slate-200" data-testid="twilio-numbers">
          <h2 className="font-serif text-xl mb-3 text-slate-900">Numéros Twilio</h2>
          {overview?.numbers?.length > 0 ? (
            <div className="space-y-2">
              {overview.numbers.map((n) => (
                <div key={n.phone_number} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-white">
                  <div>
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" /> {n.phone_number}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      SMS {n.capabilities?.SMS ? "✓" : "✗"} · MMS {n.capabilities?.MMS ? "✓" : "✗"} · Voice {n.capabilities?.voice ? "✓" : "✗"}
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300">{n.status}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic">
              Aucun numéro acheté sur ce compte. CONFILEX utilise le numéro OVH <strong>+33 4 44 05 07 57</strong> directement via Vapi (byo-phone-number), pas via Twilio.
            </div>
          )}
        </Card>

        {/* Webhook config */}
        {inboundConfig && (
          <Card className="p-5 mb-6 border-slate-200" data-testid="webhook-config">
            <h2 className="font-serif text-xl mb-1 text-slate-900">URLs des webhooks entrants</h2>
            <p className="text-sm text-slate-600 mb-3">
              {inboundConfig.instructions}
            </p>
            <div className="space-y-2">
              {[
                { label: "SMS entrants", url: inboundConfig.sms_webhook },
                { label: "WhatsApp entrants", url: inboundConfig.whatsapp_webhook },
              ].map((cfg) => (
                <div key={cfg.label} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                  <span className="text-xs font-medium text-slate-500 w-32 shrink-0">{cfg.label} :</span>
                  <code className="text-xs text-slate-800 truncate flex-1">{cfg.url}</code>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(cfg.url, cfg.label)} data-testid={`copy-${cfg.label.toLowerCase().replace(' ', '-')}`}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <div className="text-xs text-slate-500 mt-2">
                Méthode : <code>POST</code> · Signature HMAC-SHA1 :{" "}
                <strong className={inboundConfig.signature_validation ? "text-emerald-700" : "text-amber-700"}>
                  {inboundConfig.signature_validation ? "activée ✓" : "désactivée (dev mode)"}
                </strong>
              </div>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {[
            { key: "all", label: "Tous les messages", count: messages.length },
            { key: "errors", label: "Erreurs", count: errors.length, danger: true },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`twilio-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-full text-sm border transition ${
                tab === t.key
                  ? (t.danger ? "bg-rose-900 text-white border-rose-900" : "bg-slate-900 text-white border-slate-900")
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
              }`}
            >
              {t.label}
              <span className={`ml-2 text-xs ${tab === t.key ? "text-white/70" : "text-slate-400"}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {/* Messages */}
        <Card className="p-5 border-slate-200" data-testid="twilio-messages">
          {(tab === "all" ? messages : errors).length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              {tab === "errors" ? "Aucune erreur récente 🎉" : "Aucun message dans l'historique."}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {(tab === "all" ? messages : errors).map((m) => (
                <div key={m.sid} className="py-3 flex items-start gap-3" data-testid={`msg-${m.sid}`}>
                  <div className="shrink-0 mt-1">
                    {m.direction?.includes("outbound") ? (
                      <ArrowUpRight className="w-4 h-4 text-slate-500" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4 text-violet-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-900">{m.to || m.from}</span>
                      <Badge className={STATUS_BADGE[m.status] || "bg-slate-100 text-slate-700 border-slate-300"}>
                        {m.status}
                      </Badge>
                      {m.error_code && (
                        <Badge className="bg-rose-100 text-rose-900 border-rose-300">
                          <XCircle className="w-3 h-3 mr-1" /> {m.error_code}
                        </Badge>
                      )}
                      <span className="text-[10px] text-slate-400">{fmt(m.date_sent || m.date_created)}</span>
                    </div>
                    {m.body && (
                      <div className="text-sm text-slate-700 mt-1 truncate">{m.body}</div>
                    )}
                    {m.error_message && (
                      <div className="text-xs text-rose-700 mt-1">{m.error_message}</div>
                    )}
                  </div>
                  {m.price && parseFloat(m.price) !== 0 && (
                    <div className="text-xs text-slate-400 shrink-0">
                      {Math.abs(parseFloat(m.price)).toFixed(4)} {m.price_unit}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </main>
      <Footer />
    </div>
  );
}
